import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import { axiosGetCall } from '../../../services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from '../../../lib/toastMessage';


const BulkGatewayAssignmentModal = ({ open, onClose, onComplete }) => {
  const [gateways, setGateways] = useState([]);
  const [assignedGateways, setAssignedGateways] = useState([]);
  const [availableGateways, setAvailableGateways] = useState([]);
  const [selectedAssignedGatewayIds, setSelectedAssignedGatewayIds] = useState([]);
  const [selectedAvailableGatewayIds, setSelectedAvailableGatewayIds] = useState([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const loadRequestId = useRef(0);

  const selectedAssignedSet = useMemo(() => new Set(selectedAssignedGatewayIds), [selectedAssignedGatewayIds]);
  const selectedAvailableSet = useMemo(() => new Set(selectedAvailableGatewayIds), [selectedAvailableGatewayIds]);
  const allAssignedSelected = assignedGateways.length > 0 && selectedAssignedGatewayIds.length === assignedGateways.length;
  const allAvailableSelected = availableGateways.length > 0 && selectedAvailableGatewayIds.length === availableGateways.length;

  const fetchDedicatedGateways = useCallback(async (dedicatedUserId = '') => {
    const response = await axiosGetCall('getListGateway', {
      isBulkAssign: true,
      isDedicated: 1,
      ...(dedicatedUserId ? { dedicatedUserId } : {}),
    });
    return (response?.gatewayList || []).filter((gateway) => Number(gateway.is_dedicated) === 1);
  }, []);

  const getAssignedUserIds = useCallback((gateway) => {
    if (Array.isArray(gateway.d_user_id)) return gateway.d_user_id.map((id) => String(id).trim());
    if (typeof gateway.d_user_id === 'string') return gateway.d_user_id.split(',').map((id) => id.trim());
    return [];
  }, []);

  useEffect(() => {
    if (!open) return;
    setGateways([]);
    setAssignedGateways([]);
    setAvailableGateways([]);
    setSelectedAssignedGatewayIds([]);
    setSelectedAvailableGatewayIds([]);
    setUserId('');
    setLoadError('');
    setHasLoaded(false);
  }, [open]);

  const loadGatewaysForUser = useCallback(async (dedicatedUserId) => {
    const requestId = ++loadRequestId.current;
    setLoading(true);
    setLoadError('');
    setSelectedAssignedGatewayIds([]);
    setSelectedAvailableGatewayIds([]);
    setHasLoaded(false);
    try {
      const [allGateways, assignedFromApi] = await Promise.all([
        fetchDedicatedGateways(),
        fetchDedicatedGateways(dedicatedUserId),
      ]);
      if (requestId !== loadRequestId.current) return;
      const assignedApiIds = new Set(assignedFromApi.map((gateway) => String(gateway.gateway_id)));
      const hasAssignmentData = allGateways.some((gateway) => gateway.d_user_id !== null && gateway.d_user_id !== undefined);
      const assigned = allGateways.filter((gateway) => {
        if (hasAssignmentData) return getAssignedUserIds(gateway).includes(dedicatedUserId);
        return assignedApiIds.has(String(gateway.gateway_id));
      });
      const assignedIds = new Set(assigned.map((gateway) => String(gateway.gateway_id)));
      const available = allGateways.filter((gateway) => !assignedIds.has(String(gateway.gateway_id)));

      setGateways(allGateways);
      setAssignedGateways(assigned);
      setAvailableGateways(available);
      setHasLoaded(true);
    } catch (error) {
      if (requestId === loadRequestId.current) {
        setLoadError(error?.message || 'Unable to load gateways for this User ID.');
      }
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, [fetchDedicatedGateways, getAssignedUserIds]);

  useEffect(() => {
    if (!open) return undefined;

    const dedicatedUserId = userId.trim();
    loadRequestId.current += 1;
    setLoading(false);
    if (!dedicatedUserId) return undefined;

    const debounceTimer = setTimeout(() => {
      loadGatewaysForUser(dedicatedUserId);
    }, 1000);

    return () => {
      clearTimeout(debounceTimer);
      loadRequestId.current += 1;
    };
  }, [open, userId, loadGatewaysForUser]);

  const handleClose = () => {
    if (submitting || loading) return;
    onClose();
  };

  const toggleGateway = (gatewayId, selectedIds, setSelectedIds, clearOtherSelection) => {
    const isSelected = selectedIds.includes(gatewayId);
    if (!isSelected) clearOtherSelection([]);
    setSelectedIds(isSelected
      ? selectedIds.filter((id) => id !== gatewayId)
      : [...selectedIds, gatewayId]);
  };

  const toggleAllGateways = (gatewaysToToggle, allSelected, setSelectedIds, clearOtherSelection) => {
    if (!allSelected) clearOtherSelection([]);
    setSelectedIds(allSelected ? [] : gatewaysToToggle.map((gateway) => gateway.gateway_id));
  };

  const handleSubmit = async (action) => {
    const selectedGatewayIds = action === 'assign' ? selectedAvailableGatewayIds : selectedAssignedGatewayIds;
    const trimmedUserId = userId.trim();
    if (!selectedGatewayIds.length) {
      renderErrorMessage('Select at least one gateway.');
      return;
    }
    if (!trimmedUserId) {
      renderErrorMessage('Enter a User ID.');
      return;
    }

    setSubmitting(true);
    try {
      const params = {
        gatewayId: selectedGatewayIds,
        ...(action === 'assign' ? { assignUserId: trimmedUserId } : { removeUserId: trimmedUserId }),
      };
      const response = await axiosGetCall(action === 'assign' ? 'assignGateway' : 'removeAssignGateway', params);
      renderSuccessMessage(response?.message || `${action === 'assign' ? 'Assigned' : 'Removed'} ${selectedGatewayIds.length} gateway(s).`);
      onComplete();
      setUserId('');
      setSelectedAssignedGatewayIds([]);
      setSelectedAvailableGatewayIds([]);
      onClose();
    } catch (error) {
      renderErrorMessage(error?.message || 'Unable to update gateway assignments.');
    } finally {
      setSubmitting(false);
    }
  };

  const assignedEmptyMessage = !userId.trim()
    ? 'Please enter the User ID first to load gateways.'
    : loadError
      ? 'Unable to load assigned gateways.'
      : !hasLoaded
        ? 'Gateway list will load shortly.'
        : gateways.length
          ? 'No gateways are currently assigned to this user.'
          : 'No dedicated gateways found.';
  const availableEmptyMessage = !userId.trim()
    ? 'Please enter the User ID first to load gateways.'
    : loadError
      ? 'Unable to load available gateways.'
      : !hasLoaded
        ? 'Gateway list will load shortly.'
        : gateways.length
          ? 'All dedicated gateways are already assigned to this user.'
          : 'No dedicated gateways found.';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Bulk Assign / Remove Gateway User</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            required
            label="User ID"
            value={userId}
            onChange={(event) => {
              setUserId(event.target.value);
              setLoadError('');
              setHasLoaded(false);
              setGateways([]);
              setAssignedGateways([]);
              setAvailableGateways([]);
              setSelectedAssignedGatewayIds([]);
              setSelectedAvailableGatewayIds([]);
            }}
            disabled={submitting}
          />

          {loadError && <Alert severity="error">{loadError}</Alert>}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          <>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography sx={{ px: 1.5, py: 1, fontWeight: 600, borderBottom: 1, borderColor: 'divider' }}>
                  Currently Assigned ({assignedGateways.length})
                </Typography>
                <FormControlLabel
                  sx={{ m: 0, px: 1.5, py: 0.5, width: '100%', borderBottom: 1, borderColor: 'divider' }}
                  control={
                    <Checkbox
                      checked={allAssignedSelected}
                      indeterminate={selectedAssignedGatewayIds.length > 0 && !allAssignedSelected}
                      onChange={() => toggleAllGateways(
                        assignedGateways,
                        allAssignedSelected,
                        setSelectedAssignedGatewayIds,
                        setSelectedAvailableGatewayIds,
                      )}
                      disabled={!assignedGateways.length || submitting}
                    />
                  }
                  label={<Typography fontWeight={600}>Select all</Typography>}
                />
                <List dense sx={{ maxHeight: 300, overflowY: 'auto', py: 0 }}>
                  {assignedGateways.map((gateway) => {
                    const gatewayId = gateway.gateway_id;
                    return (
                      <ListItem key={gatewayId} disablePadding>
                        <FormControlLabel
                          sx={{ m: 0, px: 1.5, width: '100%' }}
                          control={
                            <Checkbox
                              checked={selectedAssignedSet.has(gatewayId)}
                              onChange={() => toggleGateway(
                                gatewayId,
                                selectedAssignedGatewayIds,
                                setSelectedAssignedGatewayIds,
                                setSelectedAvailableGatewayIds,
                              )}
                              disabled={submitting}
                            />
                          }
                          label={`${gatewayId} - ${gateway.name || 'Unnamed Gateway'}`}
                        />
                      </ListItem>
                    );
                  })}
                  {!assignedGateways.length && (
                    <ListItem>
                      <Typography color="text.secondary">{assignedEmptyMessage}</Typography>
                    </ListItem>
                  )}
                </List>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography sx={{ px: 1.5, py: 1, fontWeight: 600, borderBottom: 1, borderColor: 'divider' }}>
                  Available to Assign ({availableGateways.length})
                </Typography>
                <FormControlLabel
                  sx={{ m: 0, px: 1.5, py: 0.5, width: '100%', borderBottom: 1, borderColor: 'divider' }}
                  control={
                    <Checkbox
                      checked={allAvailableSelected}
                      indeterminate={selectedAvailableGatewayIds.length > 0 && !allAvailableSelected}
                      onChange={() => toggleAllGateways(
                        availableGateways,
                        allAvailableSelected,
                        setSelectedAvailableGatewayIds,
                        setSelectedAssignedGatewayIds,
                      )}
                      disabled={!availableGateways.length || submitting}
                    />
                  }
                  label={<Typography fontWeight={600}>Select all</Typography>}
                />
                <List dense sx={{ maxHeight: 300, overflowY: 'auto', py: 0 }}>
                  {availableGateways.map((gateway) => {
                    const gatewayId = gateway.gateway_id;
                    return (
                      <ListItem key={gatewayId} disablePadding>
                        <FormControlLabel
                          sx={{ m: 0, px: 1.5, width: '100%' }}
                          control={
                            <Checkbox
                              checked={selectedAvailableSet.has(gatewayId)}
                              onChange={() => toggleGateway(
                                gatewayId,
                                selectedAvailableGatewayIds,
                                setSelectedAvailableGatewayIds,
                                setSelectedAssignedGatewayIds,
                              )}
                              disabled={submitting}
                            />
                          }
                          label={`${gatewayId} - ${gateway.name || 'Unnamed Gateway'}`}
                        />
                      </ListItem>
                    );
                  })}
                  {!availableGateways.length && (
                    <ListItem>
                      <Typography color="text.secondary">{availableEmptyMessage}</Typography>
                    </ListItem>
                  )}
                </List>
              </Box>
            </Box>
          </>

        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          onClick={() => handleSubmit('assign')}
          disabled={loading || submitting || Boolean(loadError) || !hasLoaded || !selectedAvailableGatewayIds.length}
        >
          {submitting ? 'Processing…' : 'Assign'}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleSubmit('remove')}
          disabled={loading || submitting || Boolean(loadError) || !hasLoaded || !selectedAssignedGatewayIds.length}
        >
          {submitting ? 'Processing…' : 'Remove'}
        </Button>
        <Button onClick={handleClose} disabled={submitting || loading}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkGatewayAssignmentModal;
